/**
 * Projects Demo Page - Static demo for showcasing the dashboard
 * Displays fake projects with real construction site images
 * Accessible without login at /projects-demo
 */

import { Card, Typography, Tag, Button, message } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import AppLayout from '@/components/AppLayout';

const { Title, Text } = Typography;
const { Meta } = Card;

// Demo data structure
interface DemoView {
  id: string;
  name: string;
  image: string;
  photoCount: number;
  lastCapture: string;
  token: string;
  device: string;
  capturing: boolean;
  interval: number;
}

interface DemoProject {
  id: string;
  name: string;
  views: DemoView[];
}

// Hardcoded demo projects
const demoProjects: DemoProject[] = [
  {
    id: 'proj-1',
    name: 'Downtown Tower Construction',
    views: [
      {
        id: 'view-1',
        name: 'Main Entrance',
        image: '/demo-images/sim_3.jpg',
        photoCount: 2847,
        lastCapture: '14/12, 10:32',
        token: 'DTW-001',
        device: 'Samsung S21',
        capturing: true,
        interval: 300,
      },
      {
        id: 'view-2',
        name: 'East Wing',
        image: '/demo-images/sim_9.jpg',
        photoCount: 1923,
        lastCapture: '14/12, 10:30',
        token: 'DTW-002',
        device: 'Pixel 7',
        capturing: true,
        interval: 600,
      },
      {
        id: 'view-3',
        name: 'Rooftop',
        image: '/demo-images/sim_6.jpg',
        photoCount: 856,
        lastCapture: '14/12, 10:28',
        token: 'DTW-003',
        device: 'Samsung A54',
        capturing: false,
        interval: 300,
      },
    ],
  },
  {
    id: 'proj-2',
    name: 'Riverside Office Complex',
    views: [
      {
        id: 'view-4',
        name: 'Foundation',
        image: '/demo-images/sim_7.jpg',
        photoCount: 4521,
        lastCapture: '14/12, 10:31',
        token: 'ROC-001',
        device: 'Pixel 8 Pro',
        capturing: true,
        interval: 180,
      },
      {
        id: 'view-5',
        name: 'Parking Area',
        image: '/demo-images/sim_15.jpg',
        photoCount: 1287,
        lastCapture: '14/12, 10:25',
        token: 'ROC-002',
        device: 'Samsung S23',
        capturing: true,
        interval: 600,
      },
    ],
  },
];

// Format interval in seconds to human-readable string
function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

export default function ProjectsDemoPage() {
  const handleDemoAction = () => {
    message.info('This is a demo page - actions are disabled');
  };

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Title level={2}>Projects</Title>
          <Tag color="blue">Demo Mode</Tag>
        </div>
        <Button
          type="primary"
          icon={<MaterialIcon name="add" />}
          onClick={handleDemoAction}
        >
          New Project
        </Button>
      </div>

      {/* Projects List */}
      <div className="flex flex-col gap-6">
        {demoProjects.map((project) => (
          <div key={project.id}>
            {/* Project Card Header */}
            <Card
              className="mb-4"
              styles={{
                body: { padding: 0 },
                header: {
                  backgroundColor: 'var(--ant-gray-6)',
                  borderBottom: '1px solid var(--ant-color-split)',
                },
              }}
              title={
                <div className="flex items-center justify-between">
                  <Text strong className="text-base">{project.name}</Text>
                  <Button
                    type="text"
                    icon={<MaterialIcon name="more_vert" />}
                    onClick={handleDemoAction}
                  />
                </div>
              }
            />

            {/* Views Grid */}
            <div className="view-cards-grid">
              {project.views.map((view) => (
                <Card
                  key={view.id}
                  hoverable
                  cover={
                    <div
                      className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative"
                      onClick={handleDemoAction}
                    >
                      <img
                        src={view.image}
                        alt={`${view.name} timelapse`}
                        className="w-full h-full object-cover"
                      />
                      {/* Top overlay: photo count (left) and last capture time (right) */}
                      <div className="absolute top-2 left-2 flex gap-1">
                        <Tag
                          style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: 'none', color: 'white' }}
                          icon={<MaterialIcon name="photo_library" size={14} />}
                        >
                          {view.photoCount.toLocaleString()} Photos
                        </Tag>
                      </div>
                      <Tag
                        className="absolute top-2 right-2"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: 'none', color: 'white' }}
                        icon={<MaterialIcon name="schedule" size={14} />}
                      >
                        {view.lastCapture}
                      </Tag>
                      {/* Bottom overlay: token (left) and device model (right) */}
                      <Tag
                        className="absolute bottom-2 left-2"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: 'none', color: 'white' }}
                        icon={<MaterialIcon name="key" size={14} />}
                      >
                        {view.token}
                      </Tag>
                      <Tag
                        className="absolute bottom-2 right-2"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: 'none', color: 'white' }}
                        icon={<MaterialIcon name="phone_android" size={14} />}
                      >
                        {view.device}
                      </Tag>
                    </div>
                  }
                  actions={[
                    <Button
                      key="device"
                      type="text"
                      icon={<MaterialIcon name="phonelink_setup" size={22} />}
                      onClick={handleDemoAction}
                    >
                      Settings
                    </Button>,
                    <Button
                      key="test-shot"
                      type="text"
                      icon={<MaterialIcon name="photo_camera" size={22} />}
                      onClick={handleDemoAction}
                    >
                      Test Shot
                    </Button>,
                    <Button
                      key="more"
                      type="text"
                      icon={<MaterialIcon name="more_vert" size={22} />}
                      onClick={handleDemoAction}
                    />,
                  ]}
                >
                  <Meta
                    title={view.name}
                    description={
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {view.capturing ? (
                          <Tag
                            color="success"
                            icon={<MaterialIcon name="play_circle" />}
                            style={{ width: '100%', justifyContent: 'center' }}
                          >
                            Capturing every {formatInterval(view.interval)}
                          </Tag>
                        ) : (
                          <Tag
                            color="warning"
                            icon={<MaterialIcon name="pause_circle" />}
                            style={{ width: '100%', justifyContent: 'center' }}
                          >
                            Capture paused
                          </Tag>
                        )}
                        <Tag
                          color="success"
                          icon={<MaterialIcon name="battery_horiz_075" />}
                          style={{ width: '100%', justifyContent: 'center' }}
                        >
                          87%
                        </Tag>
                        <Tag
                          color="success"
                          icon={<MaterialIcon name="hard_drive" />}
                          style={{ width: '100%', justifyContent: 'center' }}
                        >
                          45 GB free
                        </Tag>
                        <Tag
                          color="success"
                          icon={<MaterialIcon name="wifi" />}
                          style={{ width: '100%', justifyContent: 'center' }}
                        >
                          Connected
                        </Tag>
                      </div>
                    }
                  />
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
